import { useTranslation } from 'react-i18next';
import { ErrorState, LoadingState, StaleBanner } from '../../../components/ui/AsyncState';
/**
 * NotificationTimeline.tsx
 *
 * Lazy chronological event timeline for a single notification.
 * Rendered only when a history table row is expanded.
 */

import { useRecoveryQuery as useQuery } from '../../../components/ui/useRecoveryQuery';
import { historyQueryPolicy } from '../../../api/queryPolicies';
import { getNotificationTimeline } from '../../../api/endpoints/admin';
import type { NotificationHistoryItem, NotificationTimelineEvent } from '../../../api/types';
import {
  notificationLabel,
  channelTheme,
  formatAbsoluteTime,
} from './notificationPresentation';
import styles from './ShipmentNotifications.module.css';

interface NotificationTimelineProps {
  item: NotificationHistoryItem;
}

function TimelineEventRow({ event }: { event: NotificationTimelineEvent }) {
  const statusResult =
    event.status ? notificationLabel(event.status, null) : null;
  const channel =
    event.channel ? channelTheme(event.channel) : null;

  return (
    <div className={styles.timelineEventRow}>
      <div className={styles.timelineEventDot} />
      <div className={styles.timelineEventBody}>
        <div className={styles.timelineEventHeader}>
          <span className={styles.timelineEventType}>
            {(event.eventType ?? 'EVENT').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
          </span>
          {statusResult && (
            <span
              className={styles.timelineBadge}
              style={{ color: statusResult.color, background: statusResult.bgColor }}
            >
              {statusResult.label}
            </span>
          )}
          {channel && (
            <span
              className={styles.timelineBadge}
              style={{ color: channel.color, background: channel.bgColor }}
            >
              {channel.icon} {channel.label}
            </span>
          )}
        </div>
        <div className={styles.timelineEventMeta}>
          <span className={styles.timelineEventTime}>
            {formatAbsoluteTime(event.providerTimestamp ?? event.occurrenceTime ?? event.recordedTime)}
          </span>
          {event.reasonCode && (
            <span className={styles.timelineReasonCode}>{event.reasonCode}</span>
          )}
        </div>
        {event.details && (
          <div className={styles.timelineEventDetails}>{event.details}</div>
        )}
      </div>
    </div>
  );
}

export function NotificationTimeline({ item }: NotificationTimelineProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'admin',
      'notification',
      'timeline',
      item.id,
      // invalidate only when telemetryVersion changes — avoids spurious refetches
      item.telemetryVersion,
    ],
    queryFn: () => getNotificationTimeline(item.id),
    ...historyQueryPolicy,
  });

  if (isLoading) {
    return (
      <LoadingState label={t('shipments.notificationTimeline.loading')} />
    );
  }

  if (isError && !data) {
    return (
      <ErrorState title={t('shipments.notificationTimeline.loadError')} onRetry={() => refetch()} />
    );
  }

  const events = data?.events ?? data?.items ?? [];

  if (!data || events.length === 0) {
    return (
      <div className={styles.timelineContainer}>
        {isError && <StaleBanner onRetry={() => refetch()} />}
        <div className={styles.timelineEmpty}>
          {t('shipments.notificationTimeline.empty')}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.timelineContainer}>
      {isError && <StaleBanner onRetry={() => refetch()} />}
      <div className={styles.timelineTitle}>
        {t('shipments.notificationTimeline.title')}
        <span className={styles.timelineVersion}>{t('shipments.notificationTimeline.version', { version: data.telemetryVersion })}</span>
      </div>
      <div className={styles.timelineEvents}>
        {events.map((ev) => (
          <TimelineEventRow key={ev.eventId} event={ev} />
        ))}
      </div>
    </div>
  );
}
